
import {throwError as observableThrowError, of as observableOf,  Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { SETTINGS } from '../../app.constants';
import { Location } from '../models/location.model';
import { Appointment } from '../models/appointment.model';
import { Log } from '../utils/log';
import { Utils } from '../utils/utils';
import { Url } from '../models/url/url.model';
import { UrlParam } from '../models/url/url-param.model';
import { UrlParams } from '../models/url/url-params.model';
import { SessionService } from './session.service';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SlotAvailability } from '../models/slot-availability.model';

@Injectable()
export class AppointmentService {

    constructor(private http: HttpClient, private session: SessionService) {}

    /**
     * Gets all appointments for a location
     * @param location
     */
    public getAllAppointments(location: Location): Observable<Array<Appointment>> {

        // Initially token may not be available
        const options = {
            headers: new HttpHeaders({
            'Authorization': this.session.token || ''
            })
        };

        let uri:string = '';

        if (environment.useStaticAPIData) {
            uri = SETTINGS.staticTestAPIRoot + 'getAllAppointments.json';
        } else {
            uri = environment.uriEndpoint + 'locations/' + location.id + '/appointments';
        }

        return this.http.get(uri, options).pipe(
                map((res:any) => {
                    if (res && res.appoinments) {
                        let appointments:Array<Appointment> = Array<Appointment>();
                        // TODO: check json for correct array property
                        res.appoinments.forEach((element:any) => {
                            let appointment:Appointment = new Appointment(element);
                            appointments.push(appointment);
                        });
                        if(appointments.length === 0) {
                            Log.warning('Zero length appointments');
                        }
                        return appointments;
                    } else {
                        return null;
                    }
                },catchError(error => this.handleError(error)))
        );
    }

    /**
     * Schedules an appointment for a user
     * @param {Appointment}
     * @returns {Observable<Appointment>}
     * TODO: handle time slots and start time
     */
    public createAppointment(appointment: Appointment): Observable<number> {
        //let body = Utils.JSONStringify(appointment);
        // TODO: need to weave in different data values to produce what this service needs.
        // hardcoded for now inside appointment.
        let body = JSON.stringify(appointment.toJSON());

        const options = {
            headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Access-Control-Expose-Headers': 'Authorization, X-Custom',
            'Authorization': this.session.token || ''
            }),
            observe: 'response' as 'body'
        };

        if (environment.useStaticAPIData) {
            // test data, return what is requested
            return observableOf(1);
        } else {
            return this.http.post(environment.uriEndpoint + 'locations/' + appointment.location.id + '/appointments', body, options)
                .pipe(
                    map((res:any) => {
                            let lastParam:string = res.headers.get('location').split('/').pop();
                            let newAppointmentId:number = Number(lastParam);
                            return newAppointmentId;

                    }, catchError(this.handleError))
                );
        }
    }

    /**
     * Delete appointment lock
     * This endpoint has been removed (this will not be suported)
     * @param appointment
     */
    // public deleteAppointmentLock(appointment: Appointment): Observable<boolean> {

    //     let headers = new Headers();
    //     headers.append('Content-Type', 'application/json');
    //     headers.append('Authorization', this.session.token);
    //     let options = new RequestOptions({headers: headers});

    //     if (SETTINGS.useStaticAPIData) {
    //         // test data, return that delete op succeeded
    //         return Observable.of(true);
    //     } else {
    //         return this.http.delete(SETTINGS.uriEndpoint + 'locations/' + appointment.location.id
    //                                     + '/appointments/' + appointment.id + '/unlock', options)
    //                 .map(res => {
    //                     if(res.status === 200 || res.status === 204) {
    //                         return true;
    //                     } else {
    //                         Log.error('Error deleting appointment lock');
    //                         return false;
    //                     }
    //                 })
    //                 .catch(this.handleError);
    //     }

    // }

    /**
     * Delete appointment
     * @param appointment
     */
    public deleteAppointment(appointment: Appointment): Observable<boolean> {

        const options = {
            headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': this.session.token
            })
        };

        if (environment.useStaticAPIData) {
            // test data, return that delete op succeeded
            return observableOf(true);
        } else {
            return this.http.delete(environment.uriEndpoint + 'locations/' + appointment.location.id
                                        + '/appointments/' + appointment.id, options)
                .pipe(
                    map(res => {
                        if(res) {
                            return true;
                        } else {
                            Log.error('Error deleting appointment');
                            return false;
                        }
                    }, catchError(this.handleError))
                );
        }

    }

    /**
     * Gets appointment for a location
     * @param location
     */
    public getAppointment(locationID:string, appointmentID: string, applicationId:string): Observable<Appointment> {

        const options = {
            headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': this.session.token || ''
            })
        };

        let url: Url;

        let params:UrlParams = new UrlParams();

        params.add(new UrlParam('applicationCode',  applicationId ));

        if (environment.useStaticAPIData) {
            url = new Url('getAppointment.json');
        } else {
            url = new Url('locations/' + locationID + '/appointments/' + appointmentID, params);
        }

        return this.http.get(url.toString(), options).pipe(
                map(res => {
                    return new Appointment(res);
                }, catchError(this.handleError))
        );
    }

    public getSoonestAvailableAppointment(locationID:number): Observable<Date> {
        // Initially token may not be available
        const options = {
            headers: new HttpHeaders({
            'Authorization': this.session.token || ''
            })
        };

        let url: Url;
        let params: UrlParams = new UrlParams();
        //orderBy=soonest&limit=1&locationId=1
        params.add(new UrlParam('orderBy', 'soonest'));
        params.add(new UrlParam('limit', 1));

        if (locationID === environment.REMOTE_LOCATION_ID) {
            params.add(new UrlParam('remote', true));
        } else {
            params.add(new UrlParam('locationId', locationID));
        }
        params.add(new UrlParam('minimum', 1));

        if (environment.useStaticAPIData) {
            url = new Url('getAppointment.json');
        } else {
            url = new Url('slots', params);
        }

        return this.http.get(url.toString(), options).pipe(
                map((res:any) => {
                    if (res.length > 0 && res[0].startTimestamp) {
                        return Utils.parseISOLocal(res[0].startTimestamp);
                    } else {
                        return null;
                    }
                }, catchError(this.handleError))
        );
    }

    /**
     * Gets appointment for a given appCode
     * // TODO: this needs to be checked against Josh's api when he pushes it
     * @param {string} appCode the app code passed in the query param to search for an appointment by
     */
    public getAppointmentByCode(appCode: string): Observable<Appointment> {

        const options = {
            headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': this.session.token || ''
            })
        };

        let url: Url;
        let params: UrlParams = new UrlParams();
        let codeParam: UrlParam = new UrlParam('applicationCode', appCode);
        params.add(codeParam);

        if (environment.useStaticAPIData) {
            if (this.session.appointmentType === 'new') {
                return observableOf(null);
            } else {
                url = new Url('getAppointment.json');
            }
        } else {
            url = new Url('appointments', params);
        }

        return this.http.get(url.toString(), options).pipe(
                map((res:any) => {
                    if (res) {
                        let appointments: Appointment[] = new Array<Appointment>();
                        res.forEach((appt: any) => {
                            appointments.push(new Appointment(appt));
                        });

                        let returnAppointment: Appointment = null;
                        appointments.forEach(appointment => {
                            if (!returnAppointment) {
                                returnAppointment = appointment;
                            } else if (returnAppointment.updateDate < appointment.updateDate) {
                                returnAppointment = appointment;
                            }
                        });
                        return returnAppointment;
                    } else {
                        return null;
                    }
                }, catchError(this.handleError))
        );
    }

    /**
     * Update appointment
     * @param appointment
     */
    public updateAppointment(appointment: Appointment): Observable<boolean> {

        let body:any =  Utils.JSONStringify(appointment);
        const options = {
            headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': this.session.token || ''
            })
        };

        if (environment.useStaticAPIData) {
            // test data, return what is requested
            return observableOf(true);
        } else {
            let url:string = environment.uriEndpoint + 'locations/' + appointment.location.id + '/appointments/' +appointment.id;
            return this.http.put(url, body, options).pipe(
                    map(res => {
                        return true;
                    }, catchError(this.handleError))
            );
        }
    }

    /**
     * Create appointment lock
     * @param appointment
     */
    // public createAppointmentLock(appointment: Appointment): Observable<boolean> {
    //     // TODO: check results of stringify
    //     let body =  Utils.JSONStringify(appointment);
    //     let headers = new Headers();
    //     headers.append('Content-Type', 'application/json');
    //     headers.append('Authorization', this.session.token);
    //     let options = new RequestOptions({headers: headers});

    //     if (SETTINGS.useStaticAPIData) {
    //         // test data, return what is requested
    //         return Observable.of(true);
    //     } else {
    //         return this.http.post(SETTINGS.uriEndpoint + 'locations/' + appointment.location.id + '/appointments/'
    //                                 + appointment.id + '/lock', body, options)
    //                 .map(res => {
    //                     if(res.status === 200 || res.status === 201) {
    //                         return true;
    //                     } else {
    //                         Log.error('Error posting appointment lock');
    //                         return null;
    //                     }
    //                 })
    //                 .catch(this.handleError);
    //     }
    // }

    public getAppointmentAvailabilitySummary(locationID:number): Observable<SlotAvailability> {
        // Initially token may not be available
        const options = {
            headers: new HttpHeaders({
            'Authorization': this.session.token || ''
            })
        };

        let url: Url;
        let params: UrlParams = new UrlParams();

        if (locationID === environment.REMOTE_LOCATION_ID) {
            params.add(new UrlParam('remote', true));
        } else {
            params.add(new UrlParam('locationId', locationID));
        }
        url = new Url('slot-availability', params);

        return this.http.get(url.toString(), options).pipe(
                map((res:any) => {
                    let slotAvailability = new SlotAvailability();
                    if (res) {
                        if (res.lastPublishedDate) {
                            slotAvailability.lastPublishedDate = Utils.parseISOLocal(res.lastPublishedDate);
                        }

                        if (res.availableSlots.length > 0 && res.availableSlots[0].startTimestamp) {
                            slotAvailability.availableDate = Utils.parseISOLocal(res.availableSlots[0].startTimestamp);
                        }
                    }

                    return slotAvailability;
                }, catchError(this.handleError))
        );
    }
    /**
     *
     * @param error
     */
    private handleError(error: any) {
        console.log('Errorr : ' + error);
        let errMsg = '';
        if (error.message) {
            errMsg = error.message;
        } else if (error.status) {
            if (error.statusText) {
                errMsg = `${error.status} - ${error.statusText}`;
                Log.error(error.statusText);
            } else {
                errMsg = 'Server error';
            }
        } else {
            errMsg = 'Server error';
        }

        return observableThrowError(errMsg);
    }

}
